import React, { useEffect, useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import { StepperPageHeader } from "components/common/stepperPage";
import AddRounded from "@mui/icons-material/AddRounded";
import ForumRounded from "@mui/icons-material/ForumRounded";
import HelpRounded from "@mui/icons-material/HelpRounded";
import { useNavigate } from "react-router-dom";
import ChartMindHelpDialog from "./ChartMindHelpDialog";

/**
 * One-time nudge toward the walkthrough, shown on the Help button itself.
 *
 * A dialog was the other option and was rejected: a first-time provider already
 * clears the four-screen ProfessionalOnboardingDialog before reaching ChartMind,
 * and a second modal on the same page load is friction at the exact moment a
 * clinician wants to start an encounter. Pointing at the button also teaches
 * where help lives, which a dialog does not.
 *
 * localStorage rather than the user doc, matching the other `chartmind:` prefs
 * in useChartMindSession. Nothing here is PHI.
 */
const HELP_HINT_KEY = "chartmind:help-hint-seen";
const HELP_HINT_TEXT = "First time here? Watch a quick walkthrough";
const HELP_HINT_DELAY_MS = 600; // let the header settle before it appears
const HELP_HINT_DURATION_MS = 8000;

/**
 * Two separate Tooltips rather than one with a toggled `open`: MUI warns when a
 * component flips between controlled and uncontrolled, and the hint is
 * controlled while the plain "Help" label is not. Distinct keys keep them as
 * distinct instances.
 */
const HelpButton = ({ hintOpen, onOpenHelp }) => {
  const button = (
    <IconButton
      size="small"
      onClick={onOpenHelp}
      aria-label="Help"
      sx={{ color: "text.secondary" }}
    >
      <HelpRounded fontSize="small" />
    </IconButton>
  );

  return hintOpen ? (
    <Tooltip key="hint" open arrow title={HELP_HINT_TEXT}>
      {button}
    </Tooltip>
  ) : (
    <Tooltip key="plain" title="Help">
      {button}
    </Tooltip>
  );
};

// Moved to chartMindSteps so the paired phone's remote reads the same step list, labels and
// Next gating the footer does. Still re-exported at the bottom: half the ChartMind tree imports
// STEPS from here.
import { STEPS, getStepLabels, getVisibleStepOrder } from "./chartMindSteps";

const ChartMindHeader = ({
  currentStep,
  setCurrentStep,
  isRecording,
  recordingTime,
  ddxLoading,
  hasDiagnoses,
  diagnosticPlanLoading,
  hasDiagnosticPlan,
  treatmentPlanLoading,
  hasTreatmentPlan,
  chartLoading,
  hasNote,
  hasTranscript,
  hasSelectedDiagnosis,
  visitedSteps,
  onNewEncounter = undefined,
  hasEncounterData = false,
  // [BETA-CM0427] When true, hides Plan + Confirm steps and renames Treatment → "Plan". Remove when beta ends.
  cm0427Mode = false,
  // Inpatient encounters rename Recording and Treatment.
  inpatient = false,
  // The Events note type: Event, Differential, Plan, Chart. hasTranscript then means "has events".
  eventsNote = false,
  // False where the chartmind-pearls prompt is not set up, which drops the Pearls step.
  pearlsAvailable = true,
}) => {
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpHintOpen, setHelpHintOpen] = useState(false);

  // Marked seen as soon as it is scheduled, not when it is dismissed, so
  // navigating away mid-hint still spends the one showing it was allotted.
  useEffect(() => {
    let alreadySeen = true;
    try {
      alreadySeen = window.localStorage.getItem(HELP_HINT_KEY) === "1";
    } catch {
      // Private browsing with storage blocked: skip the hint rather than
      // showing it on every single visit.
    }
    if (alreadySeen) return undefined;

    try {
      window.localStorage.setItem(HELP_HINT_KEY, "1");
    } catch {
      /* ignore */
    }

    const showTimer = setTimeout(() => setHelpHintOpen(true), HELP_HINT_DELAY_MS);
    const hideTimer = setTimeout(
      () => setHelpHintOpen(false),
      HELP_HINT_DELAY_MS + HELP_HINT_DURATION_MS,
    );
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  const handleOpenHelp = () => {
    setHelpHintOpen(false);
    setHelpOpen(true);
  };

  // [BETA-CM0427] Plan + Confirm are hidden in CM0427 mode; Event shows only on the Events note.
  const visibleStepOrder = getVisibleStepOrder(cm0427Mode, inpatient, pearlsAvailable, eventsNote);

  // Map step values to indices for Stepper
  const getActiveStepIndex = () => {
    return visibleStepOrder.indexOf(currentStep);
  };

  const handleStepClick = (step) => {
    if (!isStepDisabled(step)) {
      setCurrentStep(step);
    }
  };

  // Helper: Check if a step should be disabled
  // Only allow navigating to steps that have been visited via "Next" button
  const isStepDisabled = (step) => {
    // Recording, or Event on the Events note, is always available
    if (step === STEPS.RECORD || step === STEPS.EVENTS) return false;

    // Diagnosis requires transcript and must be visited
    if (step === STEPS.DIAGNOSIS) {
      return !hasTranscript || !visitedSteps.has(STEPS.DIAGNOSIS);
    }

    // Chart can be skipped to from anywhere as long as there's a transcript —
    // the Chart step itself offers a "Generate Note" button for early generation.
    if (step === STEPS.CHART) {
      return !hasTranscript;
    }

    // All other steps: must have been visited via Next button
    return !visitedSteps.has(step);
  };

  // Step labels — keyed by step so filtering stays consistent.
  const stepLabelMap = getStepLabels(cm0427Mode, inpatient, eventsNote);

  const actions = (
    <>
      <HelpButton hintOpen={helpHintOpen} onOpenHelp={handleOpenHelp} />
      <Tooltip title="PeerView Consultation">
        <IconButton
          size="small"
          onClick={() => navigate("/dashboard/peerview")}
          sx={{ color: "text.secondary" }}
        >
          <ForumRounded fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="New Encounter">
        <IconButton
          size="small"
          onClick={onNewEncounter}
          sx={{ color: "text.secondary" }}
        >
          <AddRounded fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );

  return (
    <>
      <StepperPageHeader
        steps={visibleStepOrder.map((step) => ({
          key: step,
          label: stepLabelMap[step],
        }))}
        activeStep={getActiveStepIndex()}
        onStepClick={(step) => handleStepClick(step.key)}
        isStepDisabled={(step) => isStepDisabled(step.key)}
        // A step counts as done once the NEXT one has been visited — reaching a
        // step is not the same as finishing it.
        isStepCompleted={(step, index) => {
          const nextStep = visibleStepOrder[index + 1];
          return Boolean(nextStep && visitedSteps.has(nextStep));
        }}
        actions={actions}
      />
      <ChartMindHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
};

export { STEPS };
export default ChartMindHeader;
